import styles from "./AddressSelectPill.module.css";
import type { AddressSelectPillProps } from "./AddressSelectPill.types";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

export const AddressSelectPill = ({
  value,
  onValueChange,
  onChange,
  options,
  ariaLabel = "Select address",
  disabled = false,
  className,
  visibleOptionsCount = 5,
}: AddressSelectPillProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const selectedLabel = useMemo(() => {
    const found = options.find((o) => o.value === value);
    return found?.label || value || "Select address";
  }, [options, value]);

  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen((v) => !v);

  const emitChange = (nextValue: string) => {
    onValueChange?.(nextValue);
    if (onChange) {
      // Minimal event-like object to support existing handlers that only use `target.value`.
      onChange({
        target: { value: nextValue } as EventTarget & HTMLSelectElement,
      } as ChangeEvent<HTMLSelectElement>);

      setIsOpen(false);
    }
  };

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      const target = e.target as Node | null;
      if (target && rootRef.current.contains(target)) return;
      close();
    };

    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onDocKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onDocKeyDown);
    };
  }, []);

  const OPTION_ROW_PX = 36;
  const maxMenuHeight = Math.max(1, visibleOptionsCount) * OPTION_ROW_PX;

  return (
    <div
      ref={rootRef}
      className={[styles.pill, className].filter(Boolean).join(" ")}
      aria-label={ariaLabel}
    >
      <span className={styles.icon} aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 22s7-5.2 7-12a7 7 0 1 0-14 0c0 6.8 7 12 7 12Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      </span>

      <button
        className={styles.select}
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={styles.selectedText} title={selectedLabel}>
          {selectedLabel}
        </span>
      </button>

      {isOpen && !disabled && (
        <div className={styles.menu} role="listbox" style={{ maxHeight: maxMenuHeight }}>
          {options.map((opt) => {
            const isActive = opt.value === value;
            const isAddNew = opt.value === "__add__";
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isActive}
                className={[
                  styles.menuItem,
                  isAddNew ? styles.menuItemAdd : "",
                  isActive ? styles.menuItemActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  emitChange(opt.value);
                  close();
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}

      <span className={styles.chevron} aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  );
};
